import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, Calculator, MessageCircle } from 'lucide-react';
import { useData } from '../context/DataContext';

const ROI: React.FC = () => {
  const { openQuoteModal, settings } = useData();

  const [truckCost, setTruckCost] = useState(85000);
  const [monthlyRevenue, setMonthlyRevenue] = useState(6000);
  const [fuelCost, setFuelCost] = useState(1500);
  const [maintenanceCost, setMaintenanceCost] = useState(500);
  const [driverCost, setDriverCost] = useState(800);

  const data = useMemo(() => {
    const monthlyExpense = fuelCost + maintenanceCost + driverCost;
    const monthlyProfit = monthlyRevenue - monthlyExpense;

    // Generate 5 year projection
    const projection = [];
    let cumulativeProfit = -truckCost; // Initial investment

    for (let year = 1; year <= 5; year++) {
      cumulativeProfit += (monthlyProfit * 12);
      projection.push({
        year: `Year ${year}`,
        Revenue: monthlyRevenue * 12 * year,
        Expenses: (monthlyExpense * 12 * year) + truckCost, // including initial cost for simplicity in viz
        NetProfit: cumulativeProfit
      });
    }
    return projection;
  }, [truckCost, monthlyRevenue, fuelCost, maintenanceCost, driverCost]);

  const breakEvenMonths = Math.ceil(truckCost / (monthlyRevenue - (fuelCost + maintenanceCost + driverCost)));

  return (
    <div className="bg-gray-50 min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-gray-900 uppercase tracking-wider mb-4">Investment ROI Calculator</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">Visualize your break-even point and long-term profitability with our premium trucks.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Inputs */}
          <div className="bg-white p-6 rounded-sm shadow-lg space-y-6">
            <h3 className="font-bold text-xl mb-4 flex items-center text-automax-red"><DollarSign className="mr-2" /> Cost Variables</h3>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-1">Truck Purchase Price ($)</label>
              <input type="number" value={truckCost} onChange={(e) => setTruckCost(Number(e.target.value))} className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-2 rounded focus:ring-2 focus:ring-automax-red focus:border-transparent font-bold" />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-1">Est. Monthly Revenue ($)</label>
              <input type="number" value={monthlyRevenue} onChange={(e) => setMonthlyRevenue(Number(e.target.value))} className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-2 rounded focus:ring-2 focus:ring-automax-red focus:border-transparent font-bold" />
            </div>

            <div className="border-t pt-4">
              <label className="block text-sm font-bold text-gray-900 mb-2">Monthly Expenses ($)</label>
              <div className="space-y-3">
                <div>
                  <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Fuel</span>
                  <input type="number" value={fuelCost} onChange={(e) => setFuelCost(Number(e.target.value))} className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-2 rounded text-sm font-bold" />
                </div>
                <div>
                  <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Maintenance</span>
                  <input type="number" value={maintenanceCost} onChange={(e) => setMaintenanceCost(Number(e.target.value))} className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-2 rounded text-sm font-bold" />
                </div>
                <div>
                  <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Driver Salary</span>
                  <input type="number" value={driverCost} onChange={(e) => setDriverCost(Number(e.target.value))} className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-2 rounded text-sm font-bold" />
                </div>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="lg:col-span-2 bg-white p-6 rounded-sm shadow-lg flex flex-col">
            <h3 className="font-bold text-xl mb-6 flex items-center text-automax-red"><TrendingUp className="mr-2" /> 5-Year Profit Projection</h3>

            <div className="w-full h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="year" tick={{ fill: '#4B5563' }} />
                  <YAxis tick={{ fill: '#4B5563' }} formatter={(value) => `$${Number(value) / 1000}k`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                    itemStyle={{ color: '#111827' }}
                    formatter={(value) => [`$${Number(value).toLocaleString()}`, undefined]}
                  />
                  <Legend />
                  <Bar dataKey="Revenue" fill="#111827" name="Revenue" />
                  <Bar dataKey="Expenses" fill="#9CA3AF" name="Expenses" />
                  <Bar dataKey="NetProfit" fill="#D61F26" name="Net Profit" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-sm border border-gray-100">
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Break-Even Point</p>
                <p className="text-2xl font-bold text-automax-red">{breakEvenMonths} Months</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider">5-Year Net Profit</p>
                <p className="text-2xl font-bold text-green-600">${Math.max(0, data[4].NetProfit).toLocaleString()}</p>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <button
                onClick={() => openQuoteModal('quote')}
                className="w-full bg-automax-red hover:bg-red-700 text-white font-bold py-4 rounded-sm uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
              >
                <Calculator size={20} /> REQUEST QUOTATION
              </button>

              <a
                href={`https://wa.me/${settings.phoneNumber.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noreferrer"
                className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-4 rounded-sm uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle size={20} /> CHAT WITH AUTOMAX MANAGER
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROI;
